import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import { Calendar } from "../../components/ui/calendar";
import { highlevelAppointmentsService } from "../../services/highlevelAppointmentsService";
import { CalendarCheck, MapPin, UserRound } from "lucide-react";

function adaptedAppointments(config) {
  const bookedStages = ["consult_booked", "booked", "treatment_booked"];
  return config.leads
    .filter((l) => bookedStages.includes(l.stage))
    .slice(0, 16)
    .map((l, i) => {
      const d = new Date();
      d.setDate(d.getDate() + (i % 7));
      d.setHours(9 + (i % 8), i % 2 === 0 ? 0 : 30, 0, 0);
      return {
        appointment_id: `ADP-${i}`,
        parent_name: l.parent_name,
        child_name: null,
        date_time: d.toISOString(),
        calendar_name: `${l.programme_name} — ${config.id === "dental" ? "Recall visit" : "Consultation"}`,
        assigned_staff: l.assigned_user_name,
        status: "confirmed",
        location_branch: l.branch_name,
      };
    });
}

export default function CalendarPage() {
  const { vertical, config, branchFilter } = useOutletContext();
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    if (vertical === "enrichment") {
      highlevelAppointmentsService.listAppointments().then(setAppointments);
    } else {
      setAppointments(adaptedAppointments(config));
    }
  }, [vertical, config]);

  const filtered = useMemo(
    () => appointments.filter((a) => branchFilter === "all" || a.location_branch === branchFilter),
    [appointments, branchFilter]
  );

  const bookedDays = useMemo(() => [...new Set(filtered.map((a) => a.date_time.slice(0, 10)))].map((d) => new Date(d + "T12:00:00")), [filtered]);

  const dayKey = selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}` : null;
  const dayAppointments = filtered.filter((a) => a.date_time.slice(0, 10) === dayKey).sort((a, b) => new Date(a.date_time) - new Date(b.date_time));
  const upcoming = filtered.filter((a) => new Date(a.date_time) > new Date()).sort((a, b) => new Date(a.date_time) - new Date(b.date_time)).slice(0, 6);

  const statusBadge = (s) => s === "confirmed" ? "bg-[hsl(199_78%_92%)] text-[hsl(215_84%_22%)]" : s === "showed" ? "bg-teal-100 text-teal-800" : "bg-rose-100 text-rose-700";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 animate-fade-up max-w-6xl">
      <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
        <CardContent className="p-4 flex flex-col items-center">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            modifiers={{ booked: bookedDays }}
            modifiersClassNames={{ booked: "bg-[hsl(199_78%_92%)] text-[hsl(215_84%_22%)] font-semibold rounded-md" }}
            className="rounded-xl"
            data-testid="bookings-calendar"
          />
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-2">
            <span className="h-3 w-3 rounded bg-[hsl(199_78%_92%)] inline-block" /> Days with bookings
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
        <CardContent className="p-5">
          <p className="font-semibold text-sm text-foreground mb-1">
            {selectedDate ? selectedDate.toLocaleDateString("en-SG", { weekday: "long", day: "numeric", month: "long" }) : "Pick a date"}
          </p>
          <p className="text-xs text-muted-foreground mb-4">{dayAppointments.length} booking{dayAppointments.length === 1 ? "" : "s"}</p>
          <div className="space-y-2.5" data-testid="calendar-day-appointments">
            {dayAppointments.length === 0 && (
              <div className="text-center py-8">
                <CalendarCheck className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No bookings on this day.<br />Highlighted dates have bookings.</p>
              </div>
            )}
            {dayAppointments.map((a) => (
              <div key={a.appointment_id} className="bg-secondary rounded-xl p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-foreground tabular-nums">{new Date(a.date_time).toLocaleTimeString("en-SG", { hour: "2-digit", minute: "2-digit" })}</p>
                  <Badge variant="secondary" className={`text-[10px] ${statusBadge(a.status)}`}>{a.status === "showed" ? "attended" : a.status.replace("_", " ")}</Badge>
                </div>
                <p className="text-xs font-medium text-foreground mt-1">{a.child_name || a.parent_name}</p>
                <p className="text-[11px] text-muted-foreground">{a.calendar_name}</p>
                <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.location_branch}</span>
                  <span className="inline-flex items-center gap-1"><UserRound className="h-3 w-3" /> {a.assigned_staff}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
        <CardContent className="p-5">
          <p className="font-semibold text-sm text-foreground mb-4">Upcoming {vertical === "enrichment" ? "trials" : "appointments"}</p>
          <div className="space-y-2.5" data-testid="calendar-upcoming-list">
            {upcoming.length === 0 && <p className="text-sm text-muted-foreground">Nothing upcoming for this filter.</p>}
            {upcoming.map((a) => (
              <div key={a.appointment_id} className="flex items-center gap-3 bg-secondary rounded-xl px-3 py-2.5">
                <div className="h-10 w-10 rounded-xl bg-white border border-border flex flex-col items-center justify-center shrink-0">
                  <p className="text-[9px] text-muted-foreground uppercase leading-none">{new Date(a.date_time).toLocaleDateString("en-SG", { month: "short" })}</p>
                  <p className="text-sm font-semibold tabular-nums leading-tight">{new Date(a.date_time).getDate()}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{a.child_name || a.parent_name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{new Date(a.date_time).toLocaleTimeString("en-SG", { hour: "2-digit", minute: "2-digit" })} · {a.calendar_name}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">Every booking here was approved by staff first — reminders at T-24h and T-2h go out automatically over WhatsApp.</p>
        </CardContent>
      </Card>
    </div>
  );
}
